import { type FC, type PropsWithChildren, useEffect } from 'react'

import { LoadingIndicator } from '@dhl/components'

import { type AuthPaths, logout } from '../authRoutes/authRoutes.js'
import { AuthenticationContextProvider, useAuthenticationContext } from '../model/context.js'
import { AuthenticationErrorMessagePage, AuthorizationErrorMessagePage } from './ErrorMessages.js'
import { AxiosErrorHandler } from './AxiosErrorHandler.js'

export type AuthenticationFilterProps = PropsWithChildren<{
  isAuthorized?: boolean
  isErrorLoadingProfile: boolean
  isLoadingProfile: boolean
  paths: AuthPaths
  redirectOnFailure?: boolean
  withoutRouter?: boolean
}>

export const InnerAuthenticationFilter: FC<AuthenticationFilterProps> = ({
  children,
  isAuthorized,
  isErrorLoadingProfile,
  isLoadingProfile,
  paths,
  redirectOnFailure = false,
  withoutRouter,
}) => {
  const { isUnauthorized, onFailure } = useAuthenticationContext()
  const testId = 'authentication-filter-page'

  const logoutCall = logout(paths)

  useEffect(() => {
    if (isUnauthorized && redirectOnFailure) {
      logoutCall()
    }
  }, [isUnauthorized, redirectOnFailure, logoutCall])

  useEffect(() => {
    if (!isLoadingProfile && isErrorLoadingProfile) {
      onFailure()
    }
  }, [isErrorLoadingProfile, isLoadingProfile, onFailure])

  const showLoading =
    (!isUnauthorized && (isLoadingProfile || isAuthorized === undefined)) || (isUnauthorized && redirectOnFailure)

  if (isUnauthorized && !redirectOnFailure) {
    return <AuthenticationErrorMessagePage testId={testId} withoutRouter={withoutRouter} onButtonClick={logoutCall} />
  }

  if (!isLoadingProfile && isAuthorized === false) {
    return <AuthorizationErrorMessagePage testId={testId} withoutRouter={withoutRouter} onButtonClick={logoutCall} />
  }

  return (
    <>
      {showLoading && <LoadingIndicator />}

      {isAuthorized && children}
    </>
  )
}

export const AuthenticationFilter: FC<AuthenticationFilterProps> = (props) => (
  <AuthenticationContextProvider>
    <AxiosErrorHandler>
      <InnerAuthenticationFilter {...props} />
    </AxiosErrorHandler>
  </AuthenticationContextProvider>
)
